import { useEffect, useState } from 'react';

/**
 * usePaneMaxHeight - A hook that calculates the maximum height for a pane
 * based on the window height and specified parameters.
 *
 * @param {Object} options - Configuration options
 * @param {number} [options.offset=180] - Pixels to subtract from window height (for headers, footers, etc.)
 * @param {number} [options.minHeight=300] - Minimum height in pixels
 * @param {number} [options.maxHeightPercentage=100] - Maximum percentage of available height to use (1-100)
 * @returns {string} - CSS height value with 'px' unit
 */
export function usePaneMaxHeight(options = {}) {
  // Handle both legacy offset parameter and new options object
  const config = typeof options === 'number' ? { offset: options } : options;

  const { offset = 180, minHeight = 300, maxHeightPercentage = 100 } = config;

  // Calculate max height based on window height, offset, and percentage
  const getMaxHeight = () => {
    const availableHeight = window.innerHeight - offset;
    const percentageHeight = availableHeight * (maxHeightPercentage / 100);
    return Math.max(percentageHeight, minHeight);
  };

  const [maxHeight, setMaxHeight] = useState(getMaxHeight());

  useEffect(() => {
    const handleResize = () => setMaxHeight(getMaxHeight());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return Math.round(maxHeight) + 'px';
}
